import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import { exchangeRateAPI } from '../utils/api';
import { toast } from 'sonner';

const CurrencyContext = createContext(null);

const CURRENCIES = {
  INR: { symbol: '₹', code: 'INR', name: 'Indian Rupee' },
  AED: { symbol: 'د.إ', code: 'AED', name: 'UAE Dirham' },
};

export const CurrencyProvider = ({ children }) => {
  const [currency, setCurrency] = useState(() => localStorage.getItem('sc_currency') || 'INR');
  const [exchangeRate, setExchangeRate] = useState(null);
  const [rateLoading, setRateLoading] = useState(false);

  const fetchRate = useCallback(async () => {
    setRateLoading(true);
    try {
      const { data } = await exchangeRateAPI.getRate('INR', 'AED');
      setExchangeRate(data.rate);
    } catch (_) {
      // Fallback to approximate rate if API fails
      setExchangeRate(0.044);
      toast.error('Using approximate exchange rate');
    } finally {
      setRateLoading(false);
    }
  }, []);

  useEffect(() => {
    // Auto-detect region on first visit
    const saved = localStorage.getItem('sc_currency');
    if (!saved) {
      try {
        const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
        if (tz.includes('Dubai') || tz.includes('Riyadh') || tz.includes('Asia/Muscat') || tz.includes('Qatar') || tz.includes('Bahrain') || tz.includes('Kuwait')) {
          setCurrency('AED');
        }
      } catch (_) {
        // Timezone detection not supported, default to INR
      }
    }
    fetchRate();
  }, [fetchRate]);

  useEffect(() => {
    localStorage.setItem('sc_currency', currency);
  }, [currency]);

  const convert = useCallback((amountINR) => {
    if (currency === 'INR' || !exchangeRate) return amountINR;
    return Math.round(amountINR * exchangeRate * 100) / 100;
  }, [currency, exchangeRate]);

  const convertToINR = useCallback((amount) => {
    if (currency === 'INR' || !exchangeRate) return amount;
    return Math.round(amount / exchangeRate * 100) / 100;
  }, [currency, exchangeRate]);

  const formatPrice = useCallback((amountINR) => {
    const converted = currency === 'INR' || !exchangeRate ? amountINR : Math.round(amountINR * exchangeRate * 100) / 100;
    const info = CURRENCIES[currency] || CURRENCIES.INR;
    return `${info.symbol}${converted.toLocaleString(currency === 'AED' ? 'en-AE' : 'en-IN')}`;
  }, [currency, exchangeRate]);

  const getINRAmount = useCallback((displayAmount) => {
    if (currency === 'INR' || !exchangeRate) return displayAmount;
    return Math.round(displayAmount / exchangeRate);
  }, [currency, exchangeRate]);

  const converters = useMemo(() => ({
    convert, convertToINR, formatPrice, getINRAmount,
  }), [convert, convertToINR, formatPrice, getINRAmount]);

  const value = useMemo(() => ({
    currency, setCurrency, exchangeRate, rateLoading,
    ...converters,
    currencies: CURRENCIES,
    symbol: CURRENCIES[currency]?.symbol || '₹',
  }), [currency, exchangeRate, rateLoading, converters]);

  return (
    <CurrencyContext.Provider value={value}>
      {children}
    </CurrencyContext.Provider>
  );
};

export const useCurrency = () => {
  const context = useContext(CurrencyContext);
  if (!context) throw new Error('useCurrency must be used within CurrencyProvider');
  return context;
};
